<script setup lang="ts">
/**
 * Встречи — страница «10 · Встречи» (макеты 10-1…10-7).
 *
 * Раскладка мессенджера: слева список встреч, справа заметки выбранной. Заметки
 * жили на плане и уезжали вниз всякий раз, когда список кандидатов оказывался
 * длинным (DEV-1969), — а превратились они за это время в инструмент созвона,
 * которому на странице «что я делаю сегодня» не место.
 *
 * Личные заметки — то же самое место, только без встречи: их пишут тем же
 * полем и в ту же ленту. Отдельная страница ради них означала бы два адреса, по
 * которым ищут одну и ту же мысль.
 *
 * Выбор живёт в адресе (`/meetings/:sessionId`), а не в состоянии экрана:
 * встречу пересылают ссылкой, и уведомление о приглашении ведёт прямо в неё.
 */
import { AppButton, AppModal, useToast } from '@vinprop/design-system'
import { Video } from 'lucide-vue-next'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import TrackerToolbar from '@/components/TrackerToolbar.vue'
import { useIsMobile } from '@/composables/useIsMobile'
import MeetingNotes from '@/features/meetings/components/MeetingNotes.vue'
import MeetingScheduleModal from '@/features/meetings/components/MeetingScheduleModal.vue'
import MeetingSeriesPanel from '@/features/meetings/components/MeetingSeriesPanel.vue'
import MeetingsList from '@/features/meetings/components/MeetingsList.vue'
import { useNotesStore } from '@/features/meetings/notesStore'
import { useMeetingStore } from '@/features/meetings/store'
import { useNotificationsStore } from '@/features/notifications/store'

import type { MeetingSession, ScheduleMeetingInput } from '@/features/meetings/api'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const toast = useToast()
const meetings = useMeetingStore()
const notes = useNotesStore()
const notifications = useNotificationsStore()
const isMobile = useIsMobile()

/** Адрес личной ленты: она не встреча, но открывается тем же маршрутом */
const PERSONAL = 'personal'

/**
 * Что открыто: встреча, личные заметки или ничего.
 *
 * У личных свой адрес (`/meetings/personal`), а не пустой: на телефоне список и
 * лента — два разных экрана, и без адреса у ленты личные заметки с телефона
 * было бы не открыть вовсе.
 */
const opened = computed(() => String(route.params.sessionId ?? ''))

/**
 * Открытая серия: её записи показываются вместо ленты одной встречи.
 *
 * Третье состояние экрана рядом с «встреча» и «личные заметки» — и на телефоне
 * оно ведёт себя так же: список, потом панель, назад стрелкой (DEV-2185).
 */
const seriesRootId = computed(() => String(route.params.seriesRootId ?? ''))

/** Встреча, чью ленту показываем; пусто — личные заметки */
const sessionId = computed(() => (opened.value === PERSONAL ? '' : opened.value))

const current = ref<MeetingSession | null>(null)

/**
 * Показать выбранную ленту.
 *
 * Поток открывается у любой встречи, а закрывает его сервер: у законченной
 * события как раз и начинаются — разбор записи доезжает часами позже и
 * приносит статус, состав и конспект. Когда итог окончателен, подписка
 * отвечает отказом, и клиент к ней больше не возвращается (DEV-2142).
 *
 * Идёт ли созвон, знать всё же надо: присутствие — каретки и «печатает
 * заметку…» — живёт только в идущей.
 */
const open = async (id: string) => {
  if (seriesRootId.value) {
    // У серии своей ленты нет: заметки живут в её созвонах, и панель грузит
    // их сама по раскрытию.
    current.value = null
    meetings.closeStream()
    await notes.setSession('')

    return
  }

  current.value = id ? await meetings.meetingById(id) : null

  if (current.value) {
    meetings.openStream(id, current.value.active)
  } else {
    meetings.closeStream()
  }

  await notes.setSession(id)
}

/**
 * Открытая встреча идёт за своими событиями.
 *
 * Перечитывается только карточка: открыть заново ленту и поток значило бы
 * оборвать и подписку, и чужую правку — ради строчки статуса в шапке.
 */
watch(
  () => meetings.meetingRevision,
  async () => {
    if (!sessionId.value) return

    current.value = await meetings.meetingById(sessionId.value)
  },
)

const select = (id: string) => {
  void router.push(`/meetings/${id || PERSONAL}`)
}

/** Открыть все записи серии: строка серии ведёт в её историю, а не в созвон */
const openSeries = (rootId: string) => {
  void router.push(`/meetings/series/${rootId}`)
}

/**
 * Назад к списку — только на телефоне: там это второй экран, а не колонка
 * рядом. Ведёт на `/meetings`, а не на личные заметки: «назад» из встречи в
 * чужую ленту читалось бы как переход, которого человек не просил.
 */
const backToList = () => {
  void router.push('/meetings')
}

onMounted(async () => {
  await Promise.all([meetings.refreshState(), meetings.loadMeetings()])

  // Открытая серия — тоже адрес: ссылка на неё не должна уводить ни в идущую
  // встречу, ни в личные заметки (DEV-2200).
  const blank = !opened.value && !seriesRootId.value

  // Пришёл на экран без адреса, а встреча идёт — открываем её: искать в списке
  // то, что происходит прямо сейчас, человек не должен.
  if (blank && meetings.session) {
    meetings.consumePendingOpen()
    await router.replace(`/meetings/${meetings.session.id}`)

    return
  }

  // На широком экране лента стоит рядом со списком, и пустой она не бывает: не
  // выбрано ничего — значит открыты личные заметки.
  if (blank && !isMobile.value) {
    await router.replace(`/meetings/${PERSONAL}`)

    return
  }

  await open(sessionId.value)
})

watch([opened, seriesRootId], () => {
  void open(sessionId.value)
})

/**
 * Список идёт за встречами, пока страница открыта.
 *
 * Разбор записи доезжает часами позже созвона, и до DEV-2113 «конспект готов»
 * появлялся в списке только после перезагрузки: список грузился один раз при
 * входе и больше себя не обновлял. Уведомления про встречи приходят по тому же
 * потоку, что и остальные, — по ним и перечитываем.
 */
watch(
  () => notifications.lastIncoming,
  (incoming) => {
    if (!incoming?.notificationType.startsWith('tracker_meeting_')) return

    void meetings.loadMeetings()
  },
)

/**
 * Встреча сменилась — экран идёт за ней.
 *
 * Сменяется она не только концом: рождается собственным приглашением и
 * приходит чужим, на которое нажали в баннере. Все случаи — одно событие
 * «сессия стала другой», и разводить их по обработчикам значит гарантированно
 * забыть один: до DEV-1949 забыты были два.
 */
watch(
  () => meetings.session?.id ?? '',
  async (id, was) => {
    if (!id) {
      // Встречи не стало: автор закрыл или она протухла. Открытый архив
      // остаётся на экране — читать его можно и после конца.
      if (opened.value && current.value?.active) {
        toast.info(t('meetings.session.endedToast'))
        await meetings.loadMeetings()
        await open(sessionId.value)
      }

      return
    }

    const invited = meetings.consumePendingOpen()

    // Встреча появилась там, где её не было: либо позвали и человек вошёл,
    // либо он сам позвал кого-то из личных заметок и этим её начал. И то и
    // другое значит одно — писать теперь надо в неё, а не в личную ленту.
    if ((invited || !was) && opened.value !== id) {
      await meetings.loadMeetings()
      await router.push(`/meetings/${id}`)
    }
  },
)

/**
 * Уход с экрана отпускает встречу.
 *
 * Без этого сессия оставалась бы в сторе заметок: заметка, записанная «для
 * себя» с другого экрана, ушла бы в чужой мит (DEV-1947).
 */
onBeforeUnmount(() => {
  meetings.closeStream()
  void notes.setSession('')
})

const isScheduling = ref(false)
const noMailbox = ref(false)
const scheduleForm = ref<InstanceType<typeof MeetingScheduleModal> | null>(null)

/**
 * Отказ по рабочему ящику: сервер отдаёт машинный код, а человеку нужно
 * объяснение и путь дальше — позвать коллег в общие заметки без Meet
 * (макет 12-9А).
 */
const isNoMailbox = (failure: unknown): boolean => {
  const message = failure instanceof Error ? failure.message : String(failure ?? '')
  const payload = (failure as { response?: { data?: { message?: string } } })?.response?.data
    ?.message

  return `${message} ${payload ?? ''}`.includes('no_workspace_mailbox')
}

const schedule = async (input: ScheduleMeetingInput) => {
  try {
    const meeting = await meetings.schedule(input)

    isScheduling.value = false
    toast.success(t('meetings.schedule.done'))
    await router.push(`/meetings/${meeting.id}`)
  } catch (failure) {
    scheduleForm.value?.stopSaving()

    if (isNoMailbox(failure)) {
      isScheduling.value = false
      noMailbox.value = true

      return
    }

    toast.error(t('meetings.schedule.failed'))
  }
}

/**
 * «Позвать» — старый путь без Meet: встречу рождает первое приглашение
 * (DEV-1947). Ведёт в личные заметки, где стоит пикер участников.
 */
const inviteWithoutMeet = () => {
  noMailbox.value = false
  void router.push(`/meetings/${PERSONAL}`)
}

/**
 * На телефоне колонки не помещаются рядом: сперва список, потом лента, назад —
 * стрелкой. Показываем ровно одно из двух.
 */
const showList = computed(() => !isMobile.value || (!opened.value && !seriesRootId.value))
const showNotes = computed(() => !isMobile.value || !!opened.value || !!seriesRootId.value)
</script>

<template>
  <TrackerToolbar :breadcrumbs="[{ label: t('nav.meetings') }]">
    <AppButton variant="secondary" size="small" @click="inviteWithoutMeet()">
      {{ t('meetings.actions.inviteNow') }}
    </AppButton>

    <AppButton size="small" @click="isScheduling = true">
      <template #icon><Video :size="16" aria-hidden="true" /></template>
      {{ t('meetings.actions.plan') }}
    </AppButton>
  </TrackerToolbar>

  <div class="meetings" :class="{ 'meetings--mobile': isMobile }">
    <MeetingsList
      v-if="showList"
      :meetings="meetings.meetings"
      :selected-id="sessionId"
      :selected-series-id="seriesRootId"
      :is-loading="meetings.isListLoading"
      @select="select"
      @select-series="openSeries"
    />

    <template v-if="showNotes">
      <button v-if="isMobile" type="button" class="meetings__back" @click="backToList()">
        ← {{ t('meetings.back') }}
      </button>

      <MeetingSeriesPanel v-if="seriesRootId" :series-root-id="seriesRootId" />
      <MeetingNotes v-else :meeting="current" />
    </template>
  </div>

  <MeetingScheduleModal
    v-if="isScheduling"
    ref="scheduleForm"
    @close="isScheduling = false"
    @submit="schedule"
  />

  <AppModal
    v-if="noMailbox"
    model-value
    :title="t('meetings.mailbox.title')"
    size="small"
    @update:model-value="noMailbox = false"
  >
    <p class="meetings__mailbox">{{ t('meetings.mailbox.body') }}</p>

    <template #footer>
      <AppButton variant="secondary" @click="noMailbox = false">
        {{ t('meetings.mailbox.ok') }}
      </AppButton>

      <AppButton @click="inviteWithoutMeet()">
        {{ t('meetings.mailbox.inviteWithout') }}
      </AppButton>
    </template>
  </AppModal>
</template>

<style scoped>
.meetings {
  /* Ровно остаток экрана под тулбаром: скроллятся колонки, а не страница —
     длинный протокол не должен уводить список встреч за нижний край. */
  display: flex;
  gap: 20px;
  height: calc(100vh - 48px);
  overflow: hidden;
  padding: 20px 24px;
}

.meetings--mobile {
  flex-direction: column;
  gap: 12px;
  padding: 12px;
}

.meetings--mobile :deep(.mlist) {
  flex: 1;
  width: 100%;
  padding-right: 0;
  border-right: none;
}

.meetings__mailbox {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
  line-height: 20px;
}

.meetings__back {
  align-self: flex-start;
  padding: 4px 8px;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}
</style>
